/**
 * Collapses duplicate polling across browser tabs of the same origin.
 *
 * Every open tab runs its own `refetchInterval` timers independently — TanStack Query has no
 * concept of "another tab already has this". Two tabs of the same signed-in user therefore
 * cost the server twice for identical data, and the office-IP problem this whole module
 * exists to fix (see `backoff.ts`) gets worse per tab someone leaves open.
 *
 * NOT a permanent "leader tab" election. A fixed leader is fragile in exactly the cases that
 * matter here: it has to notice its own tab closing, and a leader parked on one screen covers
 * none of a follower's screen-specific queries (Overview's health poll means nothing to a tab
 * sitting on Devices). Instead: whichever tab's timer fires FIRST for a given query key wins
 * that round — it fetches normally and broadcasts the result. A tab whose own timer was about
 * to fire for the SAME key within the same window sees the fresh broadcast and skips its
 * redundant request, adopting the broadcast data instead (`pollInterval` in `backoff.ts` is
 * where that skip happens). A tab closing needs no handoff: the next tick with no broadcast
 * in the window just fetches as if it had always been alone. Self-healing, no election state.
 */
import type { QueryClient } from '@tanstack/react-query';

const CHANNEL_NAME = 'lumen-poll-sync';

const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null;

interface SyncMessage {
  /** `JSON.stringify(queryKey)` — the wire form, since a key is only ever compared by value. */
  key: string;
  data: unknown;
  at: number;
}

/** Last time ANY tab (including this one) saw a fresh answer for a key. */
const lastSeenAt = new Map<string, number>();

/**
 * Wires incoming broadcasts into this tab's own cache. Call once, from `Providers`.
 *
 * A broadcast is written with `setQueryData`, never through a re-fetch — the whole point is
 * that this tab does NOT make a request to learn this. `setQueryData` does not itself invoke
 * `QueryCache`'s `onSuccess` (only a real fetch completion does — see `query-core`), so this
 * cannot echo the same update back out and bounce between tabs.
 */
export function attachTabSync(queryClient: QueryClient): () => void {
  if (!channel) return () => {};
  const handler = (event: MessageEvent<SyncMessage>): void => {
    const { key, data, at } = event.data;
    lastSeenAt.set(key, at);
    try {
      queryClient.setQueryData(JSON.parse(key) as readonly unknown[], data);
    } catch {
      /* a key this build cannot parse back — a stale tab on an old deploy. Ignore; this tab
         still has its own timer and will just fetch normally next tick. */
    }
  };
  channel.addEventListener('message', handler);
  return () => channel.removeEventListener('message', handler);
}

/** Call from the query cache's global success handler, for interval-polled queries only. */
export function announceFetch(queryKey: readonly unknown[], data: unknown): void {
  const key = JSON.stringify(queryKey);
  const at = Date.now();
  lastSeenAt.set(key, at);
  channel?.postMessage({ key, data, at } satisfies SyncMessage);
}

/**
 * True if ANY tab already refreshed this key within `windowMs`. `pollInterval` uses this to
 * skip a tick — a best-effort collapse, not a guarantee: two tabs whose timers are more than
 * a beat apart in phase can still both fetch once in a while. That failure mode costs exactly
 * what today already costs, so there is nothing to lose in the miss case.
 */
export function recentlySeenElsewhere(queryKey: readonly unknown[], windowMs: number): boolean {
  const at = lastSeenAt.get(JSON.stringify(queryKey));
  return at !== undefined && Date.now() - at < windowMs;
}
